<template>
  <div id="panel-center" class="panel-center">
    <!-- Header -->
    <div class="center-header">
      <div class="header-left">
        <span style="font-size: 16px">📋</span>
        <div>
          <div class="header-title">Daily Report Dashboard</div>
          <div class="header-sub">{{ headerDateText }}</div>
        </div>
      </div>
      <div class="header-actions">
        <button
          class="action-btn"
          data-component-id="changelog--btn--open"
          @click.stop="openVersionPanel"
        >
          🕑 v{{ currentVersion }}
        </button>
        <button class="action-btn" @click="$emit('openCompare')">⚡ Compare</button>
      </div>
    </div>

    <!-- Changelog Modal — half-screen, professional timeline -->
    <Teleport to="body">
      <Transition name="changelog-fade">
        <div
          v-if="showVersionPanel"
          class="changelog-modal-backdrop"
          data-component-id="changelog--modal--backdrop"
          @click="closeVersionPanel"
        >
          <div
            class="changelog-modal"
            data-component-id="changelog--modal--main"
            role="dialog"
            aria-modal="true"
            aria-labelledby="changelog-heading"
            @click.stop
          >
            <header class="changelog-modal-header">
              <div class="changelog-modal-title">
                <span class="changelog-modal-icon">📋</span>
                <div>
                  <div id="changelog-heading" class="changelog-modal-heading">
                    Riwayat Versi & Changelog
                  </div>
                  <div class="changelog-modal-sub">
                    {{ changelog.length }} versi · saat ini
                    <span class="changelog-modal-sub-current">v{{ currentVersion }}</span>
                  </div>
                </div>
              </div>
              <button
                class="changelog-modal-close"
                data-component-id="changelog--btn--close"
                aria-label="Tutup changelog"
                @click="closeVersionPanel"
              >×</button>
            </header>

            <div class="changelog-legend">
              <span class="legend-item legend-new"><span class="legend-dot"></span>Fitur Baru</span>
              <span class="legend-item legend-upd"><span class="legend-dot"></span>Peningkatan</span>
              <span class="legend-item legend-fix"><span class="legend-dot"></span>Perbaikan Bug</span>
              <span class="legend-item legend-del"><span class="legend-dot"></span>Dihapus</span>
            </div>

            <div class="changelog-modal-body" data-component-id="changelog--modal--body">
              <ol class="changelog-timeline">
                <li
                  v-for="(entry, i) in changelog"
                  :key="entry.version"
                  class="changelog-entry"
                  :class="[
                    'status-' + entry.status,
                    { 'is-expanded': isVersionExpanded(entry.version) },
                  ]"
                  :data-component-id="`changelog--entry--v${entry.version}`"
                >
                  <div class="changelog-rail" aria-hidden="true">
                    <span class="changelog-dot"></span>
                    <span v-if="i < changelog.length - 1" class="changelog-line"></span>
                  </div>

                  <article class="changelog-card">
                    <button
                      type="button"
                      class="changelog-card-toggle"
                      :aria-expanded="isVersionExpanded(entry.version)"
                      @click="toggleVersion(entry.version)"
                    >
                      <div class="changelog-meta">
                        <span class="changelog-version">v{{ entry.version }}</span>
                        <span class="changelog-status" :class="`status-pill-${entry.status}`">
                          {{ statusLabel(entry.status) }}
                        </span>
                        <time
                          class="changelog-date"
                          :datetime="entry.date"
                          :title="`Tanggal publish v${entry.version}`"
                        >
                          <span class="changelog-date-icon" aria-hidden="true">📅</span>
                          <span class="changelog-date-label">Publish</span>
                          <span class="changelog-date-value">{{ formatDate(entry.date) }}</span>
                        </time>
                      </div>
                      <h3 class="changelog-title-text">{{ entry.title }}</h3>
                      <div class="changelog-counts">
                        <span
                          v-if="grouped(entry).feat.length"
                          class="count-pill count-new"
                          :title="`${grouped(entry).feat.length} fitur baru`"
                        >
                          <span class="count-dot"></span>{{ grouped(entry).feat.length }} Baru
                        </span>
                        <span
                          v-if="grouped(entry).refactor.length"
                          class="count-pill count-upd"
                          :title="`${grouped(entry).refactor.length} update`"
                        >
                          <span class="count-dot"></span>{{ grouped(entry).refactor.length }} Update
                        </span>
                        <span
                          v-if="grouped(entry).fix.length"
                          class="count-pill count-fix"
                          :title="`${grouped(entry).fix.length} bug fix`"
                        >
                          <span class="count-dot"></span>{{ grouped(entry).fix.length }} Fix
                        </span>
                        <span
                          v-if="grouped(entry).remove.length"
                          class="count-pill count-del"
                          :title="`${grouped(entry).remove.length} dihapus`"
                        >
                          <span class="count-dot"></span>{{ grouped(entry).remove.length }} Hapus
                        </span>
                        <span class="changelog-chevron" :class="{ 'is-open': isVersionExpanded(entry.version) }">▾</span>
                      </div>
                    </button>

                    <div v-if="isVersionExpanded(entry.version)" class="changelog-details">
                      <section class="changelog-section section-summary">
                        <header class="section-header">
                          <span class="section-marker section-marker-summary">i</span>
                          <h4 class="section-title">Ringkasan Rilis</h4>
                        </header>
                        <p class="section-summary-text">{{ entry.title }}</p>
                      </section>

                      <section v-if="grouped(entry).feat.length" class="changelog-section section-new">
                        <header class="section-header">
                          <span class="section-marker section-marker-new">+</span>
                          <h4 class="section-title">Fitur Baru</h4>
                          <span class="section-count">{{ grouped(entry).feat.length }}</span>
                        </header>
                        <ul class="section-list">
                          <li v-for="(ch, ci) in grouped(entry).feat" :key="`feat-${ci}`">
                            {{ ch.text }}
                          </li>
                        </ul>
                      </section>

                      <section v-if="grouped(entry).refactor.length" class="changelog-section section-upd">
                        <header class="section-header">
                          <span class="section-marker section-marker-upd">↻</span>
                          <h4 class="section-title">Peningkatan & Update</h4>
                          <span class="section-count">{{ grouped(entry).refactor.length }}</span>
                        </header>
                        <ul class="section-list">
                          <li v-for="(ch, ci) in grouped(entry).refactor" :key="`ref-${ci}`">
                            {{ ch.text }}
                          </li>
                        </ul>
                      </section>

                      <section v-if="grouped(entry).fix.length" class="changelog-section section-fix">
                        <header class="section-header">
                          <span class="section-marker section-marker-fix">!</span>
                          <h4 class="section-title">Perbaikan Bug</h4>
                          <span class="section-count">{{ grouped(entry).fix.length }}</span>
                        </header>
                        <ul class="section-list">
                          <li v-for="(ch, ci) in grouped(entry).fix" :key="`fix-${ci}`">
                            {{ ch.text }}
                          </li>
                        </ul>
                      </section>

                      <section v-if="grouped(entry).remove.length" class="changelog-section section-del">
                        <header class="section-header">
                          <span class="section-marker section-marker-del">−</span>
                          <h4 class="section-title">Dihapus</h4>
                          <span class="section-count">{{ grouped(entry).remove.length }}</span>
                        </header>
                        <ul class="section-list">
                          <li v-for="(ch, ci) in grouped(entry).remove" :key="`del-${ci}`">
                            {{ ch.text }}
                          </li>
                        </ul>
                      </section>
                    </div>
                  </article>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Department Tabs (filtered by user's allowedDepts) -->
    <div class="dept-tabs">
      <button
        v-for="dept in visibleDepts"
        :key="dept.id"
        class="dept-tab-btn"
        :class="{ active: activeDept === dept.originalIndex }"
        :style="{ '--dept-color': dept.color }"
        @click="switchDept(dept.originalIndex)"
      >
        <span class="dept-tab-icon">{{ dept.icon }}</span>
        <span class="dept-tab-label">{{ dept.label }}</span>
      </button>
    </div>

    <!-- Sub-report Tabs (with Overview) -->
    <div class="sub-tabs">
      <button
        class="sub-tab-btn sub-tab-overview"
        :class="{ active: isOverview }"
        :style="{ '--sub-color': currentDept.color }"
        @click="switchToOverview"
      >
        Overview
      </button>
      <button
        v-for="(report, i) in currentDept.reports"
        :key="report.id"
        class="sub-tab-btn"
        :class="{ active: activeReport === i }"
        :style="{ '--sub-color': currentDept.color }"
        @click="switchReport(i)"
      >
        {{ report.label }}
      </button>
    </div>

    <!-- Content -->
    <div class="content-scroll" ref="contentScrollEl" @scroll="onContentScroll" @click="showExportDrop = false">

      <!-- ═══ OVERVIEW ═══ -->
      <template v-if="isOverview">

        <!-- Export bar for overview -->
        <div class="report-export-bar">
          <span class="report-export-label">{{ currentDept.icon }} {{ currentDept.label }} — Overview</span>
          <button class="export-ctx-btn" @click="handleExport('clipboard')">
            📋 Copy to Clipboard
          </button>
        </div>

        <!-- Loading state -->
        <div v-if="wfLoading" class="content-loading">
          <div class="loading-spinner"></div>
          <span>Memuat laporan...</span>
        </div>

        <!-- ── Overview with parsed tags (sectioned view) ── -->
        <template v-if="!wfLoading && hasOverviewTags">

          <!-- Executive Summary -->
          <div class="ov-section" v-if="parsedOverview.executiveSummary">
            <div class="ov-section-header">
              <span class="ov-section-icon">🧠</span>
              <span class="ov-section-title">Executive Summary</span>
            </div>
            <div class="ov-executive" style="white-space: normal;">
              <MarkdownRenderer :content="parsedOverview.executiveSummary" />
            </div>
          </div>

          <!-- Report Health -->
          <div class="ov-section">
            <div class="ov-section-header">
              <span class="ov-section-icon">📋</span>
              <span class="ov-section-title">Report Health</span>
              <span class="ov-section-badge">{{ readyCount }}/{{ currentDept.reports.length }} ready</span>
            </div>
            <div class="ov-health-list">
              <div
                v-for="(report, i) in currentDept.reports"
                :key="report.id"
                class="ov-health-row"
                :class="{ ready: getReportContent(report.id)?.hasData }"
                @click="switchReport(i)"
              >
                <span class="ov-health-status">
                  {{ getReportContent(report.id)?.isProcessing ? '⏳' : getReportContent(report.id)?.hasData ? '✅' : '⏳' }}
                </span>
                <span class="ov-health-name">{{ report.label }}</span>
                <span class="ov-health-state" :class="getReportContent(report.id)?.hasData ? 'ready' : 'pending'">
                  {{ getReportContent(report.id)?.isProcessing ? 'Processing' : getReportContent(report.id)?.hasData ? 'Ready' : 'Pending' }}
                </span>
                <span class="ov-health-arrow">→</span>
              </div>
            </div>
          </div>

          <!-- Todo List — accordion cards -->
          <div class="ov-section" v-if="parsedOverview.todoPoints.length">
            <div class="ov-section-header">
              <span class="ov-section-icon">🎯</span>
              <span class="ov-section-title">Todo List</span>
              <span class="ov-section-badge">{{ parsedOverview.todoPoints.length }} items</span>
            </div>
            <div class="ov-executive ov-todo ai-accordion-points ai-accordion-todo" style="white-space: normal;">
              <details
                v-for="(point, idx) in parsedOverview.todoPoints"
                :key="'ov-todo-' + idx"
                class="ai-accordion"
                :open="idx === 0"
              >
                <summary class="ai-accordion-summary">
                  <span class="ai-accordion-idx">{{ idx + 1 }}</span>
                  <span class="ai-accordion-title">{{ getPointTitle(point) }}</span>
                  <span class="ai-accordion-chevron"></span>
                </summary>
                <div class="ai-accordion-body">
                  <MarkdownRenderer :content="getPointBody(point)" />
                </div>
              </details>
            </div>
          </div>

          <!-- Todo List — legacy markdown list -->
          <div class="ov-section" v-else-if="parsedOverview.todoList">
            <div class="ov-section-header">
              <span class="ov-section-icon">🎯</span>
              <span class="ov-section-title">Todo List</span>
            </div>
            <div class="ov-executive ov-todo" style="white-space: normal;">
              <MarkdownRenderer :content="parsedOverview.todoList" />
            </div>
          </div>

          <!-- Detail section — accordion cards -->
          <div class="ov-section" v-if="parsedOverview.detailPoints.length">
            <div class="ov-section-header">
              <span class="ov-section-icon">📄</span>
              <span class="ov-section-title">Detail</span>
              <span class="ov-section-badge">{{ parsedOverview.detailPoints.length }} items</span>
            </div>
            <div class="ov-executive ov-detail ai-accordion-points ai-accordion-detail" style="white-space: normal;">
              <details
                v-for="(point, idx) in parsedOverview.detailPoints"
                :key="'ov-detail-' + idx"
                class="ai-accordion"
                :open="idx === 0"
              >
                <summary class="ai-accordion-summary">
                  <span class="ai-accordion-idx">{{ idx + 1 }}</span>
                  <span class="ai-accordion-title">{{ getPointTitle(point) }}</span>
                  <span class="ai-accordion-chevron"></span>
                </summary>
                <div class="ai-accordion-body">
                  <MarkdownRenderer :content="getPointBody(point)" />
                </div>
              </details>
            </div>
          </div>

          <!-- Detail section — raw fallback -->
          <div class="ov-section" v-else-if="parsedOverview.detail || parsedOverview.remaining">
            <div class="ov-section-header">
              <span class="ov-section-icon">📄</span>
              <span class="ov-section-title">Detail</span>
            </div>
            <div class="ov-executive ov-detail" style="white-space: normal;">
              <MarkdownRenderer :content="parsedOverview.detail || parsedOverview.remaining" />
            </div>
          </div>

        </template>

        <!-- ── Overview with raw mdFile (no tags found) ── -->
        <template v-else-if="!wfLoading && wfOverviewContent">

          <!-- Report Health -->
          <div class="ov-section">
            <div class="ov-section-header">
              <span class="ov-section-icon">📋</span>
              <span class="ov-section-title">Report Health</span>
              <span class="ov-section-badge">{{ readyCount }}/{{ currentDept.reports.length }} ready</span>
            </div>
            <div class="ov-health-list">
              <div
                v-for="(report, i) in currentDept.reports"
                :key="report.id"
                class="ov-health-row"
                :class="{ ready: getReportContent(report.id)?.hasData }"
                @click="switchReport(i)"
              >
                <span class="ov-health-status">
                  {{ getReportContent(report.id)?.isProcessing ? '⏳' : getReportContent(report.id)?.hasData ? '✅' : '⏳' }}
                </span>
                <span class="ov-health-name">{{ report.label }}</span>
                <span class="ov-health-state" :class="getReportContent(report.id)?.hasData ? 'ready' : 'pending'">
                  {{ getReportContent(report.id)?.isProcessing ? 'Processing' : getReportContent(report.id)?.hasData ? 'Ready' : 'Pending' }}
                </span>
                <span class="ov-health-arrow">→</span>
              </div>
            </div>
          </div>

          <!-- Raw mdFile content -->
          <div class="report-box">
            <div class="report-header">
              <span class="report-header-title">📊 {{ currentDept.label }} — Overview</span>
            </div>
            <div class="report-body" style="max-height: none; white-space: normal;">
              <MarkdownRenderer :content="wfOverviewContent" />
            </div>
          </div>

        </template>

        <!-- ── No overview data — show Report Health only ── -->
        <template v-else-if="!wfLoading">
          <div class="ov-section">
            <div class="ov-section-header">
              <span class="ov-section-icon">📋</span>
              <span class="ov-section-title">Report Health</span>
              <span class="ov-section-badge">{{ readyCount }}/{{ currentDept.reports.length }} ready</span>
            </div>
            <div class="ov-health-list">
              <div
                v-for="(report, i) in currentDept.reports"
                :key="report.id"
                class="ov-health-row"
                :class="{ ready: getReportContent(report.id)?.hasData }"
                @click="switchReport(i)"
              >
                <span class="ov-health-status">
                  {{ getReportContent(report.id)?.isProcessing ? '⏳' : getReportContent(report.id)?.hasData ? '✅' : '⏳' }}
                </span>
                <span class="ov-health-name">{{ report.label }}</span>
                <span class="ov-health-state" :class="getReportContent(report.id)?.hasData ? 'ready' : 'pending'">
                  {{ getReportContent(report.id)?.isProcessing ? 'Processing' : getReportContent(report.id)?.hasData ? 'Ready' : 'Pending' }}
                </span>
                <span class="ov-health-arrow">→</span>
              </div>
            </div>
          </div>
        </template>

      </template>

      <!-- ═══ WORKFLOW REPORT (from DB) ═══ -->
      <template v-else-if="currentWfReport && currentWfReport.hasData">

        <!-- Loading -->
        <div v-if="wfLoading" class="content-loading">
          <div class="loading-spinner"></div>
          <span>Memuat laporan...</span>
        </div>

        <template v-else>
          <!-- Export bar -->
          <div class="report-export-bar">
            <span class="report-export-label">📊 {{ currentReport.label }}</span>
            <button class="export-ctx-btn" @click="handleExport('clipboard')">
              📋 Copy to Clipboard
            </button>
          </div>

          <!-- Processing banner -->
          <div v-if="currentWfReport.isProcessing" class="processing-banner">
            ⏳ Laporan sedang diproses oleh AI...
          </div>

          <!-- Visual Dashboard (web-traffic only) -->
          <WebTrafficDashboard v-if="webTrafficData" :data="webTrafficData" />

          <!-- Visual Dashboard (wa-growth only) -->
          <WAGrowthDashboard v-if="waGrowthData" :data="waGrowthData" />

          <!-- Markdown report content -->
          <div class="report-box">
            <div class="report-header">
              <span class="report-header-title">📊 {{ currentReport.title || currentReport.label }}</span>
              <span class="report-header-date">{{ formatWfDate(currentWfReport.updateDate) }}</span>
            </div>
            <div class="report-body" style="max-height: none; white-space: normal;">
              <MarkdownRenderer :content="currentWfReport.mdContent" />
            </div>
          </div>
        </template>
      </template>

      <!-- ═══ REPORT WITH MARKDOWN ═══ -->
      <template v-else-if="currentReport && hasMdContent">

        <!-- Export bar -->
        <div class="report-export-bar">
          <span class="report-export-label">📝 {{ currentReport.label }}</span>
          <button class="export-ctx-btn" @click="handleExport('clipboard')">
            📋 Copy to Clipboard
          </button>
        </div>

        <!-- Markdown report box -->
        <div class="report-box">
          <div class="report-header">
            <span class="report-header-title">📝 {{ currentReport.title || currentReport.label }}</span>
            <span class="report-header-date" v-if="currentMd">
              From DB
            </span>
          </div>
          <div class="report-body" style="max-height: none; white-space: normal;">
            <MarkdownViewer
              :content="mdContent"
              :loading="mdLoading"
              :error-msg="mdError || ''"
            />
          </div>
        </div>
      </template>

      <!-- ═══ PLACEHOLDER ═══ -->
      <template v-else-if="currentReport">

        <!-- Export bar for placeholder -->
        <div class="report-export-bar">
          <span class="report-export-label">{{ currentDept.icon }} {{ currentReport.label }}</span>
          <div style="position: relative">
            <button class="export-ctx-btn disabled" disabled>
              ⬇ Export (belum tersedia)
            </button>
          </div>
        </div>

        <div class="report-placeholder">
          <div class="placeholder-icon">{{ currentDept.icon }}</div>
          <div class="placeholder-title">{{ currentReport.title || currentReport.label }}</div>
          <div class="placeholder-dept">{{ currentDept.label }}</div>
          <div class="placeholder-text">
            Report ini akan tersedia setelah data source terhubung.
          </div>
          <div class="placeholder-meta">
            <span class="placeholder-tag">{{ currentReport.id }}</span>
            <span class="placeholder-status">Menunggu Integrasi</span>
          </div>
        </div>
      </template>

      <!-- Back to Top button (overview only) -->
      <button
        v-show="isOverview && showBackToTop"
        class="back-to-top-btn"
        @click="scrollContentToTop"
        title="Kembali ke atas"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { DEPARTMENT_TABS } from '../data/constants'
import { useTimeline } from '../composables/useTimeline'
import { useActiveReport } from '../composables/useActiveReport'
import { useMarkdown } from '../composables/useMarkdown'
import { useWorkflowData } from '../composables/useWorkflowData'
import { useAuth } from '../composables/useAuth'
import { CHANGELOG, CURRENT_VERSION } from '../data/changelog'
import {
  exportAsPdf, exportAsDocx, copyToClipboard,
  buildShareText, stripMarkdown,
} from '../utils/exportUtils'
import MarkdownViewer from './MarkdownViewer.vue'
import MarkdownRenderer from './MarkdownRenderer.vue'
import WebTrafficDashboard from './WebTrafficDashboard.vue'
import { parseWebTraffic } from '../utils/parseWebTraffic'
import WAGrowthDashboard from './WAGrowthDashboard.vue'
import { parseWAGrowth } from '../utils/parseWAGrowth'

defineEmits(['openCompare'])

const { headerDateText } = useTimeline()
const {
  activeDept, activeReport, isOverview,
  currentDept, currentReport,
  switchDept, switchReport, switchToOverview,
  reportContextKey,
} = useActiveReport()

const { isAllowed } = useAuth()

// Only show department tabs the user is allowed to see
// Each entry keeps its originalIndex so switchDept() uses the correct full-array index
const visibleDepts = computed(() =>
  DEPARTMENT_TABS
    .map((dept, i) => ({ ...dept, originalIndex: i }))
    .filter(dept => isAllowed(dept.id))
)

const contentScrollEl = ref(null)
const showExportDrop = ref(false)
const showVersionPanel = ref(false)
const collapsedVersions = ref(new Set())
const showBackToTop = ref(false)

function onContentScroll() {
  if (!contentScrollEl.value) return
  showBackToTop.value = contentScrollEl.value.scrollTop > 200
}

function scrollContentToTop() {
  if (!contentScrollEl.value) return
  contentScrollEl.value.scrollTo({ top: 0, behavior: 'smooth' })
}

const changelog = CHANGELOG
const currentVersion = CURRENT_VERSION

const STATUS_LABELS = { current: 'CURRENT', stable: 'STABLE', legacy: 'LEGACY' }
function statusLabel(status) {
  return STATUS_LABELS[status] || status.toUpperCase()
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function formatDate(iso) {
  if (!iso) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso))
  if (!m) return iso
  const [, y, mo, d] = m
  const idx = parseInt(mo, 10) - 1
  if (idx < 0 || idx > 11) return iso
  return `${d}/${MONTH_ABBR[idx]}/${y}`
}

// Memoized grouping per changelog entry — entries are immutable references
const groupedCache = new WeakMap()
function grouped(entry) {
  const cached = groupedCache.get(entry)
  if (cached) return cached
  const buckets = { feat: [], refactor: [], fix: [], remove: [] }
  for (const ch of entry.changes) {
    const bucket = buckets[ch.type] || buckets.feat
    bucket.push(ch)
  }
  groupedCache.set(entry, buckets)
  return buckets
}

function isVersionExpanded(version) {
  return !collapsedVersions.value.has(version)
}
function openVersionPanel() {
  showVersionPanel.value = true
  showExportDrop.value = false
  collapsedVersions.value = new Set() // start with all versions expanded
}
function closeVersionPanel() {
  showVersionPanel.value = false
  collapsedVersions.value = new Set()
}
function toggleVersion(version) {
  const next = new Set(collapsedVersions.value)
  if (next.has(version)) next.delete(version)
  else next.add(version)
  collapsedVersions.value = next
}

function onChangelogKeydown(e) {
  if (e.key === 'Escape' && showVersionPanel.value) closeVersionPanel()
}
onMounted(() => window.addEventListener('keydown', onChangelogKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', onChangelogKeydown))

const {
  isLoading: wfLoading, loadError: wfError,
  overviewContent: wfOverviewContent,
  parsedOverview,
  hasWorkflowData, getReportContent,
} = useWorkflowData()

const { fetchMarkdown, isLoading: mdLoading, error: mdError, currentMarkdown: currentMd } = useMarkdown()
const mdContent = ref('')

const hasMdContent = computed(() => {
  return currentReport.value && currentReport.value.mdReportId
})

// Close dropdowns + fetch markdown on tab/report switch
watch(reportContextKey, async () => {
  showExportDrop.value = false
  showVersionPanel.value = false
  collapsedVersions.value = new Set()
  if (hasMdContent.value) {
    const result = await fetchMarkdown(currentReport.value.mdReportId)
    mdContent.value = result ? result.content : ''
  } else {
    mdContent.value = ''
  }
}, { immediate: true })

// Workflow data for the currently selected report
const currentWfReport = computed(() => {
  if (!currentReport.value) return null
  return getReportContent(currentReport.value.id)
})

// Parsed web traffic dashboard data (only for web-traffic report)
const webTrafficData = computed(() => {
  if (!currentReport.value || currentReport.value.id !== 'web-traffic') return null
  if (!currentWfReport.value?.mdContent) return null
  const result = parseWebTraffic(currentWfReport.value.mdContent)
  return result.success ? result : null
})

// Parsed WA Growth dashboard data (only for wa-growth report)
const waGrowthData = computed(() => {
  if (!currentReport.value || currentReport.value.id !== 'wa-growth') return null
  if (!currentWfReport.value?.mdContent) return null
  const result = parseWAGrowth(currentWfReport.value.mdContent)
  return result.success ? result : null
})

// Format updateDate from workflow
function formatWfDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const day = d.getDate().toString().padStart(2, '0')
  const months = ['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des']
  const mon = months[d.getMonth()]
  const h = d.getHours().toString().padStart(2, '0')
  const m = d.getMinutes().toString().padStart(2, '0')
  return `${day}-${mon}-${d.getFullYear()} ${h}:${m} WIB`
}

// Helpers: extract title (first line) and body (rest) from a point
function getPointTitle(point) {
  const first = point.split('\n')[0].replace(/^\*\*(.+)\*\*$/, '$1').replace(/^#+\s*/, '')
  return first || 'Item'
}
function getPointBody(point) {
  const lines = point.split('\n')
  return lines.slice(1).join('\n').trim()
}

const readyCount = computed(() =>
  currentDept.value.reports.filter(r => {
    const wf = getReportContent(r.id)
    return wf && wf.hasData
  }).length
)

// Check if overview has parsed tag sections (executive, todo, detail)
const hasOverviewTags = computed(() => {
  const p = parsedOverview.value
  return !!(p.executiveSummary || p.todoPoints.length || p.todoList || p.detailPoints.length || p.detail)
})

function handleExport(type) {
  const deptLabel = currentDept.value.label
  const reportLabel = isOverview.value ? `${deptLabel} Overview` : currentReport.value.label
  showExportDrop.value = false

  // Build share text for text-based exports
  function getShareText() {
    let summary = ''
    if (isOverview.value) {
      if (parsedOverview.value?.executiveSummary) {
        summary = stripMarkdown(parsedOverview.value.executiveSummary)
      } else if (wfOverviewContent.value) {
        summary = stripMarkdown(wfOverviewContent.value)
      } else {
        summary = currentDept.value.overview?.executive || ''
      }
    } else if (currentWfReport.value?.mdContent) {
      summary = stripMarkdown(currentWfReport.value.mdContent)
    } else if (mdContent.value) {
      summary = stripMarkdown(mdContent.value)
    } else {
      summary = ''
    }

    return buildShareText({
      title: reportLabel,
      date: headerDateText.value,
      summary,
    })
  }

  // Get raw markdown content
  function getRawMd() {
    if (isOverview.value) return wfOverviewContent.value || ''
    if (currentWfReport.value?.mdContent) return currentWfReport.value.mdContent
    if (mdContent.value) return mdContent.value
    return ''
  }

  switch (type) {
    case 'pdf':
      exportAsPdf(contentScrollEl.value, reportLabel)
      break
    case 'docx':
      exportAsDocx(contentScrollEl.value, reportLabel)
      break
    case 'clipboard':
      copyToClipboard(getRawMd() || getShareText())
      break
  }
}
</script>
